import { Input } from "@heroui/react";
import { useDeferredValue, useMemo, useState, useTransition } from "react";

export interface DeferredFilterProps {
  readonly values: readonly string[];
  readonly onSelection?: (values: readonly string[]) => void;
}

export function useDeferredComputation<T, R>(value: T, compute: (value: T) => R): [R, boolean] {
  const deferred = useDeferredValue(value);
  const result = useMemo(() => compute(deferred), [compute, deferred]);
  return [result, !Object.is(value, deferred)];
}

export function DeferredFilter({ values, onSelection }: DeferredFilterProps) {
  const [query, setQuery] = useState("");
  const [isPending, startTransition] = useTransition();
  const [filtered, isStale] = useDeferredComputation(query, (current) =>
    values.filter((value) => value.toLocaleLowerCase().includes(current.toLocaleLowerCase())),
  );

  return (
    <section aria-busy={isPending || isStale} className={isStale ? "deferred stale" : "deferred"}>
      <Input
        aria-label="Filter live signals"
        placeholder="Filter signals"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          startTransition(() => onSelection?.(filtered));
        }}
      />
      <ul>
        {filtered.map((value) => (
          <li key={value}>{value}</li>
        ))}
      </ul>
    </section>
  );
}
